import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { FileImageOutlined, VideoCameraOutlined } from '@ant-design/icons';
import ImageItem from '../../../components/ImageItem';
import { buildVideoThumbnailCandidates, shouldUseDirectVideoPreview } from '../../../utils/commonUtils';
import type { InspectionAsset } from '../../../types/common/asset';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';
import type { InspectionAssetTabKey } from '../inspectionTabs';

interface ImagesListProps {
  onOpenModal?: (imageData?: InspectionMediaData, type?: InspectionMediaType) => void;
  viewName?: string;
  images: {
    id: string;
    src: string;
    title: string;
    raw: InspectionAsset;
  }[];
  assetType: InspectionAssetTabKey;
  isMobileInspection?: boolean;
}

type ProcessedImage = ImagesListProps['images'][number] & {
  displaySrc: string;
  hasDisplaySrc: boolean;
  useDirectVideo: boolean;
};

const ImagesList: React.FC<ImagesListProps> = ({
  onOpenModal,
  viewName = '',
  images,
  assetType,
  isMobileInspection = false,
}) => {
  const isVideo = assetType === 'defectFinder';
  const useDirectVideoPreview = isVideo && shouldUseDirectVideoPreview();
  const mediaType = isVideo ? 'video' : 'image';
  const [thumbnailAttemptById, setThumbnailAttemptById] = useState<Record<string, number>>({});
  const aspectClassName = isMobileInspection ? 'aspect-[3/4]' : 'aspect-video';

  const handleItemClick = useCallback(
    (item: ProcessedImage) => {
      onOpenModal?.(
        mediaType === 'image'
          ? {
              ...item,
              imageURI: item.src,
            }
          : item,
        mediaType
      );
    },
    [onOpenModal, mediaType]
  );

  const handleThumbnailError = useCallback((id: string) => {
    setThumbnailAttemptById(prev => ({
      ...prev,
      [id]: (prev[id] ?? 0) + 1,
    }));
  }, []);

  const processedImages = useMemo<ProcessedImage[]>(
    () =>
      images.map(item => {
        if (useDirectVideoPreview) {
          return {
            ...item,
            displaySrc: item.src,
            hasDisplaySrc: Boolean(item.src),
            useDirectVideo: true,
          };
        }

        const thumbnailCandidates = isVideo ? buildVideoThumbnailCandidates(item.src) : [item.src];
        const thumbnailAttempt = thumbnailAttemptById[item.id] ?? 0;
        const displaySrc = thumbnailCandidates[thumbnailAttempt] ?? '';

        return {
          ...item,
          displaySrc,
          hasDisplaySrc: Boolean(displaySrc),
          useDirectVideo: false,
        };
      }),
    [images, isVideo, thumbnailAttemptById, useDirectVideoPreview]
  );

  const renderFallback = () =>
    isVideo ? (
      <VideoCameraOutlined className="text-3xl" style={{ fontSize: '75px' }} />
    ) : (
      <FileImageOutlined className="text-3xl" style={{ fontSize: '75px' }} />
    );

  if (!images?.length) {
    return <div className="text-center text-gray-400 py-10">There were no assets found for this view</div>;
  }

  return (
    <div className="ImagesList">
      <Row gutter={[8, 8]}>
        {processedImages.map(item => (
          <Col key={item.id} xs={12} sm={8} md={6} lg={6}>
            <div className={`relative overflow-hidden rounded bg-black group ${aspectClassName}`}>
              {item.useDirectVideo && item.hasDisplaySrc ? (
                <video
                  src={item.displaySrc}
                  className="h-full w-full cursor-pointer object-cover"
                  muted
                  playsInline
                  preload="metadata"
                  crossOrigin="use-credentials"
                  onClick={() => handleItemClick(item)}
                />
              ) : item.hasDisplaySrc ? (
                <ImageItem
                  src={item.displaySrc}
                  title={item.title}
                  onItemClick={() => handleItemClick(item)}
                  onError={() => handleThumbnailError(item.id)}
                  isVideo={isVideo}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-300">{renderFallback()}</div>
              )}
              {isVideo && (
                <div
                  onClick={() => handleItemClick(item)}
                  className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center transition-opacity duration-300 group-hover:opacity-100">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 transition hover:scale-110">
                    <div
                      className="ml-1"
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        borderLeft: '12px solid white',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ImagesList;
